import {
  CLEANUP_IFRAME_TIMEOUT_IN_SECONDS,
  DEFAULT_AUTHORIZE_TIMEOUT_IN_SECONDS,
  DEFAULT_AUTH_CLIENT,
  DEFAULT_POPUP_HEIGHT_IN_PIXELS,
  DEFAULT_POPUP_TIMEOUT_IN_MS,
  DEFAULT_POPUP_WIDTH_IN_PIXELS,
  NEW_IDP_ERROR_MESSAGE,
  POPUP_CHECK_INTERVAL_IN_MS
} from './constants';
import {
  AuthClientOptions,
  AuthenticationResult,
  ClosePopupFn,
  LoggingOptions,
  PopUpWindowOptions
} from './global';

import { GenericError, PopupCancelledError, PopupTimeoutError } from './errors';

export const parseQueryResult = (queryString: string): AuthenticationResult => {
  if (queryString.indexOf('#') > -1) {
    queryString = queryString.slice(0, queryString.indexOf('#'));
  }

  const queryParams = queryString.split('&');
  const parsedQuery: Record<string, any> = {};

  queryParams.forEach(qp => {
    const [key, val] = qp.split('=');
    parsedQuery[key] = decodeURIComponent(val);
  });

  if (parsedQuery.expires_in) {
    parsedQuery.expires_in = parseInt(parsedQuery.expires_in);
  }

  return parsedQuery as AuthenticationResult;
};

export const runIframe = (
  authorizeUrl: string,
  eventOrigin: string,
  redirect_uri_iframe: string,
  idpOrigin?: string,
  timeoutInSeconds: number = DEFAULT_AUTHORIZE_TIMEOUT_IN_SECONDS
) => {
  return new Promise<any>((res, rej) => {
    // function to be executed on receiving message event 'identity-idp-error' from new IDP
    const checkNewIDPError = (event: MessageEvent<any>) => {
      if (event.origin !== idpOrigin) return;
      // If it's valid, process the data
      if (
        event.data &&
        typeof event.data === 'string' &&
        event.data.includes(NEW_IDP_ERROR_MESSAGE)
      ) {
        rej(rejectWithLoginRequired);
        removeIframe();
      }
    };

    window.addEventListener('message', checkNewIDPError, false);

    const iframe = window.document.createElement('iframe');

    iframe.setAttribute('width', '0');
    iframe.setAttribute('height', '0');
    iframe.style.display = 'none';

    const removeIframe = () => {
      if (window.document.body.contains(iframe)) {
        window.document.body.removeChild(iframe);
      }
      clearTimeout(timeoutSetTimeoutId);
      window.removeEventListener('load', checkIDToken);
      window.removeEventListener('load', checkCodeResult);
      window.removeEventListener('message', checkNewIDPError);
    };

    const timeoutSetTimeoutId = setTimeout(() => {
      rej('TIMEOUT_ERROR');
      removeIframe();
    }, timeoutInSeconds * 1000);

    const rejectWithLoginRequired = GenericError.fromPayload({
      error: 'login_required',
      error_description: 'Login required'
    });

    window.document.body.appendChild(iframe);
    iframe.setAttribute('src', authorizeUrl);

    // function to check token in URL hash
    const checkUrlForToken = () => {
      try {
        const url = new URL(iframe.contentWindow.location.href);
        if (!url) {
          return false;
        }

        if (url.hash.includes('id_token=')) {
          const hashParams = url.hash.split('&');
          for (let i = 0; i < hashParams.length; i++) {
            if (hashParams[i].includes('id_token=')) {
              res({
                id_token: hashParams[i].split('id_token=')[1]
              });
              return true;
            }
          }
        } else if (url.hash.includes('error=')) {
          rej(rejectWithLoginRequired); // Reject only in case of error, else continue flow for new IDP
          return true;
        }
        return false;
      } catch (e) {
        // Continue flow in case of any exception for new IDP case (Will throw timeout error in worst case)
        return false;
      }
    };

    // function to check atate and code in URL query params
    const checkUrlForCode = () => {
      try {
        const searchParams = getUrlParams(iframe);
        if (!searchParams) {
          return false;
        }
        const codeResult = {
          code: searchParams?.get('code'),
          state: searchParams?.get('state')
        };
        const error = searchParams?.get('error');
        const error_description = searchParams?.get('error_description');

        if (codeResult.code) {
          res(codeResult);
          return true;
        } else if (error || error_description) {
          rej(rejectWithLoginRequired); // Reject only in case of error, else continue flow for new IDP
          return true;
        }
        return false;
      } catch (e) {
        // Continue flow in case of any exception for new IDP case (Will throw timeout error in worst case)
        return false;
      }
    };

    const checkIDToken = () => {
      const isRemoveIframe = checkUrlForToken();
      if (isRemoveIframe) {
        setTimeout(removeIframe, CLEANUP_IFRAME_TIMEOUT_IN_SECONDS * 1000);
      }
    };

    const checkCodeResult = () => {
      const isRemoveIframe = checkUrlForCode();
      if (isRemoveIframe) {
        setTimeout(removeIframe, CLEANUP_IFRAME_TIMEOUT_IN_SECONDS * 1000);
      }
    };

    if (authorizeUrl.includes('response_type=id_token')) {
      iframe.addEventListener('load', checkIDToken);
    } else {
      iframe.addEventListener('load', checkCodeResult);
    }
  });
};

export const getUrlParams = (iframe: any) => {
  try {
    const URLSearch = new URLSearchParams(iframe.contentWindow.location.search);
    return URLSearch;
  } catch (error) {
    return null;
  }
};

export const openPopup = (url: string) => {
  const POPUP_TOP_P0SITION =
    window.screenY + (window.innerHeight - DEFAULT_POPUP_HEIGHT_IN_PIXELS) / 2;
  const POPUP_LEFT_P0SITION =
    window.screenX + (window.innerWidth - DEFAULT_POPUP_WIDTH_IN_PIXELS) / 2;
  return window.open(
    url,
    'auth:authorize:popup',
    `left=${POPUP_LEFT_P0SITION},top=${POPUP_TOP_P0SITION},width=${DEFAULT_POPUP_WIDTH_IN_PIXELS},height=${DEFAULT_POPUP_HEIGHT_IN_PIXELS},resizable,scrollbars=yes,status=1`
  );
};

export const runPopup = (
  popUpOptions: PopUpWindowOptions,
  redirectUri: string,
  closePopup: ClosePopupFn
) => {
  return new Promise<AuthenticationResult>((resolve, reject) => {
    // Poll the URL of the popup to detect whether the user has logged in
    // Also poll whether the user has closed the popup without authenticating
    popUpOptions.loginWithPopupPromiseHandlers = { resolve, reject };
    popUpOptions.intervalId = setInterval(() => {
      if (popUpOptions.popupWindow && !popUpOptions.popupWindow.closed) {
        try {
          const redirectURL = new URL(redirectUri);
          const popupURL = new URL(popUpOptions.popupWindow.location);
          if (
            popupURL.host === redirectURL.host &&
            popupURL.pathname === redirectURL.pathname
          ) {
            // If URL has code & state parameters, the login is successful
            if (
              popupURL.searchParams.has('state') &&
              popupURL.searchParams.has('code')
            ) {
              const codeResult = {
                state: popupURL.searchParams.get('state'),
                code: popupURL.searchParams.get('code')
              };
              closePopup(true);
              resolve(codeResult);
            } else if (
              popupURL.searchParams.has('error') &&
              popupURL.searchParams.has('error_description')
            ) {
              // If URL has error & error_description parameters, the login has failed, return the same error
              const error = popupURL.searchParams.get('error');
              const errorDescription =
                popupURL.searchParams.get('error_description');
              closePopup(true);
              reject(new GenericError(error, errorDescription));
            }
          }
        } catch (e) {
          // Gracefully handling the read URL exception in case of cross origin
        }
      } else {
        closePopup(true);
        reject(new PopupCancelledError());
      }
    }, POPUP_CHECK_INTERVAL_IN_MS);

    popUpOptions.timeoutId = setTimeout(() => {
      popUpOptions.timeoutId = null;
      closePopup(true);
      reject(new PopupTimeoutError());
    }, DEFAULT_POPUP_TIMEOUT_IN_MS);
  });
};

export const getCrypto = () => {
  //ie 11.x uses msCrypto
  return (window.crypto || (window as any).msCrypto) as Crypto;
};

export const getCryptoSubtle = () => {
  const crypto = getCrypto();
  //safari 10.x uses webkitSubtle
  return crypto.subtle || (crypto as any).webkitSubtle;
};
export const createRandomString = (withoutSpecials = false) => {
  let charset =
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_~.';
  if (withoutSpecials) {
    charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  }
  let random = '';
  const randomValues = Array.from(
    getCrypto().getRandomValues(new Uint8Array(43))
  );
  randomValues.forEach(v => (random += charset[v % charset.length]));
  return random;
};

export const encode = (value: string) => btoa(value);
export const decode = (value: string) => atob(value);

export const createQueryParams = (params: any) => {
  return Object.keys(params)
    .filter(k => typeof params[k] !== 'undefined')
    .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
    .join('&');
};

export const sha256 = async (s: string) => {
  const digestOp: any = getCryptoSubtle().digest(
    { name: 'SHA-256' },
    new TextEncoder().encode(s)
  );

  // msCrypto (IE11) uses the old spec, which is not Promise based
  // https://msdn.microsoft.com/en-us/expression/dn904640(v=vs.71)
  // Instead of returning a promise, it returns a CryptoOperation
  // with a result property in it.
  // As a result, the various events need to be handled in the event that we're
  // working in IE11 (hence the msCrypto check). These events just call resolve
  // or reject depending on their intention.
  if ((window as any).msCrypto) {
    return new Promise((res, rej) => {
      digestOp.oncomplete = (e: any) => {
        res(e.target.result);
      };

      digestOp.onerror = (e: ErrorEvent) => {
        rej(e.error);
      };

      digestOp.onabort = () => {
        rej('The digest operation was aborted');
      };
    });
  }

  return await digestOp;
};

const urlEncodeB64 = (input: string) => {
  const b64Chars: { [index: string]: string } = { '+': '-', '/': '_', '=': '' };
  return input.replace(/[+/=]/g, (m: string) => b64Chars[m]);
};

// https://stackoverflow.com/questions/30106476/
const decodeB64 = (input: string) =>
  decodeURIComponent(
    atob(input)
      .split('')
      .map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

export const urlDecodeB64 = (input: string) =>
  decodeB64(input.replace(/_/g, '/').replace(/-/g, '+'));

export const bufferToBase64UrlEncoded = (input: number[] | Uint8Array) => {
  const ie11SafeInput = new Uint8Array(input);
  return urlEncodeB64(
    window.btoa(String.fromCharCode(...Array.from(ie11SafeInput)))
  );
};
// function takes an array of numbers or a Uint8Array,
//  converts it to a Base64 string, and then performs URL encoding on the Base64 string.
export const validateCrypto = () => {
  if (!getCrypto()) {
    throw new Error(
      'For security reasons, `window.crypto` is required to run `identity-web-sdk`.'
    );
  }
  if (typeof getCryptoSubtle() === 'undefined') {
    throw new Error(`
      identity-web-sdk must run on a secure origin.
    `);
  }
};

export const getBaseUrl = (env: string) => {
  let baseUrl: string = '';
  switch (env) {
    case 'dev':
      baseUrl = 'https://developer-dev.api.autodesk.com/authentication/v2';
      break;
    case 'int':
      baseUrl = 'https://developer-stg.api.autodesk.com/authentication-int/v2';
      break;
    case 'stg':
      baseUrl = 'https://developer-stg.api.autodesk.com/authentication/v2';
      break;
    case 'stg.afg.us': //Fedramp stagging url
      baseUrl = 'https://api-stg.afg.us.autodesk.com/authentication/v2';
      break;
    case 'prod.afg.us': //Fedramp prod url
      baseUrl = 'https://api.afg.us.autodesk.com/authentication/v2';
      break;
    case 'prod':
    default:
      baseUrl = 'https://developer.api.autodesk.com/authentication/v2';
  }
  return baseUrl;
};
export const validateOptions = (options: AuthClientOptions) => {
  if (!options.client_id) {
    throw GenericError.fromPayload({
      error: 'clientId_required',
      error_description: 'ClientId required'
    });
  }
  if (!options.redirect_uri) {
    throw GenericError.fromPayload({
      error: 'redirectUri_required',
      error_description: 'RedirectUri required'
    });
  }
};

export const getLoggingFormat = (options: LoggingOptions) => {
  const { name, version } = DEFAULT_AUTH_CLIENT;
  const sdkDetails = `${name}/${version}`;
  const methodDetails = `${options.method}/${
    options.isMethodDeprecated ? '0' : '1'
  }`;
  const config = `cache=${options.cacheLocation},refreshToken=${
    options.useRefreshTokens ? '1' : '0'
  }`;
  const uniqueRequestId = createRandomString(true).substring(0, 32);
  /*
    Logging Format:
    <SDK_Name>/<SDK_Full_Version>;<SDK_Method_Invoked>/<Is_Method_Active>;cache=<Cache_Location_Configured>,refreshToken=<IsUsingRefreshToken>;<Unique_Request_ID>
  */
  return `${sdkDetails};${methodDetails};${config};${uniqueRequestId}`;
};
